import { VideoRemakeRequestVideoSource } from "@apis/generated/openapi";
import type { BodyOf } from "@apis/http/typedRoute";

type RemakeRequest = BodyOf<"POST/api/video/remake">;
type CreditPreviewRequest = BodyOf<"POST/api/video/remake/credit-preview">;

type RemakeIdentity = Pick<RemakeRequest, "videoUrl" | "videoAssetId" | "videoId" | "videoSource">;
type CreditPreviewIdentity = Pick<CreditPreviewRequest, "videoUrl" | "videoAssetId" | "videoId" | "videoSource">;

/**
 * 参考视频身份信息，三种互斥场景：
 *
 * - `link`：用户直接输入/外链解析得到的视频（videoUrl + videoSource，videoId 可选）
 * - `upload`：用户上传的原视频（videoAssetId + videoUrl，videoSource = Kaloclip）
 * - `welcome`：Welcome 页从 URL 的 id 解析（videoId + videoSource = Kalodata，videoUrl 已回填）
 *
 * 用 `kind` 作为判别字段，避免靠字段是否存在去「猜」场景。
 */
export type ReferenceVideoIdentity =
  | { kind: "link"; videoUrl: string; videoSource: VideoRemakeRequestVideoSource; videoId?: string }
  | { kind: "upload"; videoUrl: string; videoSource: VideoRemakeRequestVideoSource; videoAssetId: number }
  | { kind: "welcome"; videoUrl: string; videoSource: VideoRemakeRequestVideoSource; videoId: string };

/** 来自 URL query / 任务详情等「松散」来源的参考视频字段，交给工厂归一化成 ReferenceVideoIdentity */
export interface LooseReferenceVideoFields {
  videoUrl?: string | null;
  videoAssetId?: number | null;
  videoId?: string | null;
  videoSource?: string | null;
}

const SUPPORTED_SOURCES = new Set<string>(Object.values(VideoRemakeRequestVideoSource));

const toVideoSource = (value: string | null | undefined): VideoRemakeRequestVideoSource | undefined =>
  value && SUPPORTED_SOURCES.has(value) ? (value as VideoRemakeRequestVideoSource) : undefined;

/**
 * 把松散字段归一化成判别联合：
 *
 * - 有正整数 videoAssetId → `upload`
 * - videoSource = Kalodata 且有 videoId → `welcome`
 * - 其余 → `link`
 *
 * 缺少必需字段（videoUrl / videoSource）时返回 null。
 */
export function classifyReferenceVideoIdentity(loose: LooseReferenceVideoFields): ReferenceVideoIdentity | null {
  const videoUrl = loose.videoUrl?.trim();
  if (!videoUrl) return null;

  const videoSource = toVideoSource(loose.videoSource);
  if (!videoSource) return null;

  const videoId = loose.videoId?.trim() || undefined;

  if (typeof loose.videoAssetId === "number" && loose.videoAssetId > 0) {
    return { kind: "upload", videoUrl, videoSource, videoAssetId: loose.videoAssetId };
  }

  if (videoSource === VideoRemakeRequestVideoSource.Kalodata && videoId) {
    return { kind: "welcome", videoUrl, videoSource, videoId };
  }

  return { kind: "link", videoUrl, videoSource, videoId };
}

/** 构造 `POST/api/video/remake` 的参考视频字段（videoUrl 必传，按场景附带 videoAssetId / videoId） */
export function buildRemakeIdentity(identity: ReferenceVideoIdentity): RemakeIdentity {
  switch (identity.kind) {
    case "upload":
      return { videoUrl: identity.videoUrl, videoAssetId: identity.videoAssetId, videoSource: identity.videoSource };
    case "welcome":
    case "link":
      return { videoUrl: identity.videoUrl, videoId: identity.videoId, videoSource: identity.videoSource };
  }
}

/**
 * 构造 `POST/api/video/remake/credit-preview` 的参考视频字段。
 *
 * welcome 场景按后端约定改传 `videoId + videoSource` 并省略 videoUrl；其余场景与 remake 一致。
 */
export function buildCreditPreviewIdentity(identity: ReferenceVideoIdentity): CreditPreviewIdentity {
  // 两个生成枚举（remake / credit-preview）成员值完全一致，但 TS 视为不同的标称枚举，这里做一次集中桥接。
  const videoSource = identity.videoSource as unknown as CreditPreviewRequest["videoSource"];
  switch (identity.kind) {
    case "upload":
      return { videoUrl: identity.videoUrl, videoAssetId: identity.videoAssetId, videoSource };
    case "welcome":
      return { videoId: identity.videoId, videoSource };
    case "link":
      return { videoUrl: identity.videoUrl, videoId: identity.videoId, videoSource };
  }
}
